// ============================================================================
// Table Columns: Teams
// Key: teams
// Generated file. Do not edit manually.
// ============================================================================

// #region Imports
import {
	cellRenderer,
	type EntityTableConfig,
	enumRenderer,
	iconEnumRenderer,
	objectRenderer,
	relationCountRenderer,
} from '../../entity-config-runtime/entity-table-config'
import type {
	CustomPageDto,
	PauseReasonSummary,
	QueueSummary,
	SkillGroupSummary,
	SkillSummary,
	TeamCustomPageResponse,
	TeamPauseReasonResponse,
	TeamQueueResponse,
	TeamSkillGroupResponse,
	TeamSkillResponse,
	TeamSummary,
	TeamUserDto,
	UserDto,
} from '../api/backstage.schemas'
import { CustomPageIcon, LinkTarget, MediaType } from '../api/backstage.schemas'
// #endregion

// #region Entity List Columns
export const teamsListColumnsConfig = {
	columns: [
		'modifiedBy',
		cellRenderer('modifiedUtc', 'dateTime'),
		'name',
		cellRenderer('isDefaultTeam', 'boolean'),
		objectRenderer('userGroup', 'name'),
		objectRenderer('responsibleUserGroup', 'name'),
		relationCountRenderer('userCount', 'users'),
		relationCountRenderer('queueCount', 'queues'),
		relationCountRenderer('skillCount', 'skills'),
		relationCountRenderer('customPageCount', 'customPages'),
		relationCountRenderer('skillGroupCount', 'skillGroups'),
	],
	actions: [
		{
			type: 'link',
			id: 'id',
			label: 'tables_actions_detail',
			icon: 'ArrowRight',
			style: 'link',
			href: (row: TeamSummary) => `/teams/${row.id}/details`,
		},
	],
} as const satisfies EntityTableConfig<TeamSummary>
// #endregion

// #region Relation List Columns
// #region Relation: CustomPages
export const teamsRelationsCustomPagesAssignedColumnsConfig = {
	columns: ['teamName', 'customPageName'],
} as const satisfies EntityTableConfig<TeamCustomPageResponse>
export const teamsRelationsCustomPagesAvailableColumnsConfig = {
	columns: [
		'displayName',
		'description',
		'url',
		iconEnumRenderer('icon', CustomPageIcon, 'CustomPageIcon'),
		cellRenderer('enabledForAllUsers', 'boolean'),
		cellRenderer('sortOrder', 'number'),
		enumRenderer('linkTarget', LinkTarget, 'LinkTarget'),
		cellRenderer('iFrameWidth', 'number'),
		'modifiedBy',
		cellRenderer('modifiedUtc', 'dateTime'),
	],
} as const satisfies EntityTableConfig<CustomPageDto>
// #endregion

// #region Relation: PauseReasons
export const teamsRelationsPauseReasonsAssignedColumnsConfig = {
	columns: [
		'teamName',
		'pauseReasonName',
		cellRenderer('pauseReasonColor', 'color'),
	],
} as const satisfies EntityTableConfig<TeamPauseReasonResponse>
export const teamsRelationsPauseReasonsAvailableColumnsConfig = {
	columns: [
		'name',
		cellRenderer('color', 'color'),
		cellRenderer('sortOrder', 'number'),
		cellRenderer('userCount', 'number'),
		cellRenderer('teamCount', 'number'),
		'modifiedBy',
		cellRenderer('modifiedUtc', 'dateTime'),
	],
} as const satisfies EntityTableConfig<PauseReasonSummary>
// #endregion

// #region Relation: Queues
export const teamsRelationsQueuesAssignedColumnsConfig = {
	columns: [
		'teamName',
		'queueName',
		'queueDescription',
		cellRenderer('isActive', 'boolean'),
		cellRenderer('isRequired', 'boolean'),
		enumRenderer('queueType', MediaType, 'MediaType'),
	],
} as const satisfies EntityTableConfig<TeamQueueResponse>
export const teamsRelationsQueuesAvailableColumnsConfig = {
	columns: [
		'name',
		'description',
		cellRenderer('wrapUpTimeSeconds', 'number'),
		cellRenderer('sortOrder', 'number'),
		'modifiedBy',
		cellRenderer('modifiedUtc', 'dateTime'),
		cellRenderer('userCount', 'number'),
		cellRenderer('contactCodeTreeCount', 'number'),
		enumRenderer('type', MediaType, 'MediaType'),
	],
} as const satisfies EntityTableConfig<QueueSummary>
// #endregion

// #region Relation: SkillGroups
export const teamsRelationsSkillGroupsAssignedColumnsConfig = {
	columns: ['teamName', 'skillGroupName'],
} as const satisfies EntityTableConfig<TeamSkillGroupResponse>
export const teamsRelationsSkillGroupsAvailableColumnsConfig = {
	columns: [
		'name',
		cellRenderer('userCount', 'number'),
		cellRenderer('teamCount', 'number'),
		cellRenderer('skillCount', 'number'),
		'modifiedBy',
		cellRenderer('modifiedUtc', 'dateTime'),
	],
} as const satisfies EntityTableConfig<SkillGroupSummary>
// #endregion

// #region Relation: Skills
export const teamsRelationsSkillsAssignedColumnsConfig = {
	columns: ['teamName', 'skillName', cellRenderer('isInherited', 'boolean')],
} as const satisfies EntityTableConfig<TeamSkillResponse>
export const teamsRelationsSkillsAvailableColumnsConfig = {
	columns: [
		'name',
		cellRenderer('userCount', 'number'),
		cellRenderer('teamCount', 'number'),
		cellRenderer('templateCount', 'number'),
		cellRenderer('skillGroupCount', 'number'),
		'modifiedBy',
		cellRenderer('modifiedUtc', 'dateTime'),
	],
} as const satisfies EntityTableConfig<SkillSummary>
// #endregion

// #region Relation: Users
export const teamsRelationsUsersAssignedColumnsConfig = {
	columns: [
		'teamName',
		cellRenderer('isResponsible', 'boolean'),
		cellRenderer('isPrimaryTeam', 'boolean'),
		'userFirstName',
		'userLastName',
		'userEmail',
		'userLogonName',
	],
} as const satisfies EntityTableConfig<TeamUserDto>
export const teamsRelationsUsersAvailableColumnsConfig = {
	columns: [
		'firstName',
		'lastName',
		'email',
		cellRenderer('isDisabled', 'boolean'),
		'logonName',
		'extension',
		'customProperty1',
		'customProperty2',
		'customProperty3',
		'customProperty4',
		'customProperty5',
	],
} as const satisfies EntityTableConfig<UserDto>
// #endregion
// #endregion

// #region Client List Columns
export const teamsClientGetColumnsConfig = {
	columns: [
		'teamName',
		cellRenderer('isResponsible', 'boolean'),
		cellRenderer('isPrimaryTeam', 'boolean'),
		'userFirstName',
		'userLastName',
		'userEmail',
		'userLogonName',
	],
} as const satisfies EntityTableConfig<TeamUserDto>
// #endregion

// #region Columns Config Export
export const teamsColumnsConfig = {
	list: teamsListColumnsConfig,
	relations: {
		customPages: {
			assigned: teamsRelationsCustomPagesAssignedColumnsConfig,
			available: teamsRelationsCustomPagesAvailableColumnsConfig,
		},
		pauseReasons: {
			assigned: teamsRelationsPauseReasonsAssignedColumnsConfig,
			available: teamsRelationsPauseReasonsAvailableColumnsConfig,
		},
		queues: {
			assigned: teamsRelationsQueuesAssignedColumnsConfig,
			available: teamsRelationsQueuesAvailableColumnsConfig,
		},
		skillGroups: {
			assigned: teamsRelationsSkillGroupsAssignedColumnsConfig,
			available: teamsRelationsSkillGroupsAvailableColumnsConfig,
		},
		skills: {
			assigned: teamsRelationsSkillsAssignedColumnsConfig,
			available: teamsRelationsSkillsAvailableColumnsConfig,
		},
		users: {
			assigned: teamsRelationsUsersAssignedColumnsConfig,
			available: teamsRelationsUsersAvailableColumnsConfig,
		},
	},
	client: {
		get: teamsClientGetColumnsConfig,
	},
} as const
// #endregion
